// Frontend estimator helpers. The quote math itself lives in the shared,
// unit-tested `convex/lib/quote.ts` so the browser prices exactly what the
// Stripe checkout charges.
import type { Bucketed, ChannelScan } from "../../convex/lib/scan";
import { RATES, tierLimitBytes, type BoostTier } from "../../convex/lib/quote";

export const GB = RATES.bytesPerGB;

/** Coerce a stored Discord premium_tier (0–3 / null) into a BoostTier. */
export function asBoostTier(tier: number | null | undefined): BoostTier | undefined {
  if (tier === 0 || tier === 1 || tier === 2 || tier === 3) return tier;
  return undefined;
}

/** Human label for a boost level, e.g. "Level 2 · 50 MB per file". */
export function tierLabel(tier: number | null | undefined): string {
  const t = asBoostTier(tier);
  if (t === undefined) return "Level 0 · 10 MB per file";
  const mb = Math.round(tierLimitBytes(t) / (1024 * 1024));
  return `Level ${t} · ${mb} MB per file`;
}

export const ALL_CONTENT = {
  text: true,
  pics: true,
  video: true,
  files: true,
} as const;

// ── Message slider scale ─────────────────────────────────────────────────────
// The message slider must reach the $100 floor (1,000 messages) and give real
// granularity in the common 1k–50k range without racing to enterprise volumes.
// A log scale does exactly that: equal slider travel = equal % change, so
// 1k→2k and 100k→200k feel the same. Enterprises connect for an exact quote, so
// the visible top is a sensible ~250k.
export const MSG_SLIDER = { min: 1_000, max: 250_000, steps: 1_000 } as const;

/** Slider position (0…steps) → a friendly message count on a log curve. */
export function sliderPosToMessages(pos: number): number {
  const { min, max, steps } = MSG_SLIDER;
  const p = Math.max(0, Math.min(steps, Math.round(pos)));
  if (p <= 0) return min; // exact floor — must be selectable ($100)
  if (p >= steps) return max;
  const raw = min * Math.pow(max / min, p / steps);
  // Round to a friendly value: nearest 100 below 10k, nearest 1,000 above.
  return raw < 10_000 ? Math.round(raw / 100) * 100 : Math.round(raw / 1_000) * 1_000;
}

/** Inverse: a message count → the nearest slider position (for initial value). */
export function messagesToSliderPos(messages: number): number {
  const { min, max, steps } = MSG_SLIDER;
  const m = Math.max(min, Math.min(max, messages));
  return Math.round((Math.log(m / min) / Math.log(max / min)) * steps);
}

/** Uniform bucket (same value across every time window). */
function uniform(count: number, bytes: number): Bucketed {
  const m = () => ({ count, bytes });
  return { allTime: m(), last1y: m(), last6mo: m(), last90d: m() };
}

/**
 * A synthetic one-"channel" scan built from the pre-connect slider inputs, so
 * the marketing estimator can reuse the exact same `computeQuote`. Recency is
 * unknown before a scan, so every time window carries the same totals.
 */
export function syntheticScan(input: {
  messages: number;
  picsGB: number;
  videoGB: number;
  filesGB: number;
}): ChannelScan[] {
  return [
    {
      id: "synthetic",
      name: "your workspace",
      isPrivate: false,
      text: uniform(input.messages, 0),
      pics: uniform(0, input.picsGB * GB),
      video: uniform(0, input.videoGB * GB),
      files: uniform(0, input.filesGB * GB),
    },
  ];
}

/** Compact byte label, e.g. 2_400_000_000 → "2.4 GB", 12_000_000 → "12 MB". */
export function formatBytes(bytes: number): string {
  if (bytes >= GB) return `${(bytes / GB).toFixed(bytes >= 10 * GB ? 0 : 1)} GB`;
  if (bytes >= 1_000_000) return `${Math.round(bytes / 1_000_000)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
}

/** Compact count, e.g. 17326 → "17.3K", 1_200_000 → "1.2M". */
export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `${n}`;
}
